import React, { ChangeEventHandler, useRef } from 'react';
import { Grid, InputGroup, List, ListItem, Skeleton, Stack, FormControl, InputProps } from '@chakra-ui/react';
import { InputField } from '../InputField';

interface ListItemProps {
  items: { value: string; name: string }[];
  onItemSelect: (e: Event, item: string) => void;
  loading?: boolean;
  notFoundSection?: any;
  [T: string]: any;
}

const ListItems = ({ loading, items, onItemSelect, notFoundSection, ...props }: ListItemProps) => {
  if (!loading) {
    if (items.length > 0) {
      return (
        <>
          {items?.map((item) => (
            <ListItem
              {...props}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  // @ts-ignore
                  e.target?.nextElementSibling?.focus();
                  e.preventDefault();
                }
                if (e.key === 'ArrowUp') {
                  // @ts-ignore
                  e.target?.previousElementSibling?.focus();
                  e.preventDefault();
                }
                if (e.key === 'Enter') {
                  onItemSelect(e as unknown as Event, item?.value);
                }
              }}
              key={`list-${item?.value}`}
              as="li"
              cursor="pointer"
              onClick={(e) => onItemSelect(e as unknown as Event, item?.value)}
              display="block"
              padding={2}
              fontSize={13}
              value={item?.value}
              _hover={{ bg: 'gray.50', color: 'inherit' }}
              _focus={{ bg: 'gray.50', color: 'inherit' }}
            >
              {item?.name}
            </ListItem>
          ))}
        </>
      );
    }
    // No items found
    return (
      <ListItem
        as="li"
        display="block"
        paddingLeft={5}
        paddingRight={5}
        paddingTop={3}
        paddingBottom={3}
        fontSize="13px"
        bg="gray.50"
        role="tab"
      >
        {notFoundSection}
      </ListItem>
    );
  }
  return (
    <Stack spacing={3} margin="2px 2px">
      <Skeleton height="12" aria-selected="false" role="tab" />
      <Skeleton height="12" aria-selected="false" role="tab" />
      <Skeleton height="12" aria-selected="false" role="tab" />
    </Stack>
  );
};

export interface AutocompleteInputProps {
  items: { value: string; name: string }[];
  children?: any;
  placeholder?: string;
  value: string;
  setValue: (e: React.FormEvent<HTMLDivElement>, value: string) => void;
  onItemSelect: (e: Event, item: string) => void;
  loading?: boolean;
  selectedItem?: string;
  notFoundSection?: any;
  label?: string;
  errorText?: string;
  autoComplete?: string;
  id?: string;
  key?: string;
  inputProps?: InputProps | undefined;
}

/**
 * An input autocomplete component.
 *
 * At the moment, the user *must* select an autocompleted value to proceed.
 *
 * Please see the story file if you want help with google maps API.
 */
const AutocompleteInput = ({
  placeholder = 'Search for an address…',
  items,
  value,
  setValue,
  onItemSelect,
  selectedItem,
  loading,
  notFoundSection = 'No matches found',
  label,
  errorText,
  autoComplete,
  id = 'autocomplete-input',
  children,
  inputProps,
  ...props
}: AutocompleteInputProps) => {
  const ref = useRef(null);
  const listRef = useRef(null);

  return (
    <FormControl>
      <Grid gridTemplateColumns="1fr" gridRowGap={2}>
        <InputGroup>
          <InputField
            label={label}
            id={id}
            ref={ref}
            paddingInlineStart="11px"
            placeholder={placeholder}
            paddingInline="0"
            isInvalid={!!errorText}
            errorText={errorText}
            inputProps={{ value: selectedItem || value, autoComplete, ...inputProps }}
            onKeyDown={(e) => {
              // If backspace and selected item
              if (selectedItem && e.key === 'Backspace') {
                // Clear selected item
                setValue(e, selectedItem);
                onItemSelect(e as unknown as Event, '');
              } else if (e.key === 'ArrowDown') {
                const item = (listRef?.current as unknown as HTMLElement)?.querySelector('li');
                item?.focus();
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              // @ts-ignore Event target does have value
              setValue(e, e.target.value);
            }}
            {...props}
          />
        </InputGroup>
        {value && !selectedItem && (
          <List
            ref={listRef}
            border="solid 1px"
            borderColor="gray.300"
            borderRadius="4px"
            boxShadow="0 1px 4px 0 rgba(0, 0, 0, 0.15)"
            role="tablist"
          >
            <ListItems
              role="tab"
              margin="2px 2px"
              notFoundSection={notFoundSection}
              loading={loading}
              items={items}
              onItemSelect={onItemSelect}
            />
          </List>
        )}
      </Grid>
    </FormControl>
  );
};

export * from './util/index';
export default AutocompleteInput;
